import { Injectable, OpaqueToken, Inject } from "@angular/core";
import { URLSearchParams } from "@angular/http";
import { JwtHttpClientService } from "../../http/jwt-http-client.service";
import { AbstractHttpService } from "../../http/abstract-http.service";
import { ConfigurationService } from "../../configuration/configuration.service";
import { ResourceModule } from "../resource.module";
/**
 * Generic service for searching an resource from the server.
 *
 * The #SEARCH_RESOURCE must be registered as a provider in the Angular dependency injection to specify the resource
 * name. This will form part of the URL path
 *
 * Errors are returned as a #DataClientErrorMessage
 */
export class SearchSortField {
    constructor(field, dir) {
        this.field = field;
        this.dir = dir;
    }
}
export class SearchService extends AbstractHttpService {
    /**
     * Constructs the service with a resource, Http service, and Error service.
     * The resource may be injected via the #SEARCH_RESOURCE alias
     * @param http HTTP Service
     * @param configService
     * @param nameSpace NameSpace that forms part of the URL
     * @param searchNames Search names for the APP
     * @param resource Resource name that forms part of the URL
     * @param version Resource version that forms part of the URL
     */
    constructor(http, configService, nameSpace, searchNames, resource, version) {
        super();
        this.http = http;
        this.configService = configService;
        this.nameSpace = nameSpace;
        this.searchNames = searchNames;
        this.resource = resource;
        this.version = version;
        if (searchNames && searchNames.indexOf(resource) < 0) {
            throw new Error("Search " + resource + " is not supported.");
        }
        let url = SearchService.URL_PREFIX + nameSpace + "/" + resource;
        if (version) {
            url = url + ".v" + version;
        }
        url = url + "/";
        this.url = url;
    }
    getNameSpace() {
        return this.nameSpace;
    }
    getResource() {
        return this.resource;
    }
    /**
     * Retrieves the search results
     * @param filter Object containing the filter. All properties of the object will be used to build the search request.
     * @param start record to start result
     * @param limit limit of number of records to retrieve
     * @param sort required sort fields
     * @returns {Observable<R>}
     */
    search(filter, start, limit, sort) {
        let params = new URLSearchParams();
        if (limit) {
            params.set('limit', limit + "");
        }
        if (start) {
            params.set('start', start + "");
        }
        if (filter) {
            let filterRequest = this.buildFilterRequest(filter);
            if (filterRequest) {
                params.set("filter", filterRequest);
            }
        }
        if (sort) {
            let sortRequest = this.buildSortRequest(sort);
            if (sortRequest) {
                params.set("sort", sortRequest);
            }
        }
        let opts = { search: params };
        return this.http.get(this.url, opts)
            .map((response) => response.json() || {})
            .catch((response) => this.handleServiceError(response)).share();
    }
    /**
     * Build the filter request in a http get format. When the value is not a number, it will wrap in quotes. Also it will nest property names when it finds an object.
     * i.e. primaryCategory:{name:'Wine'} will become a parameter with the name primaryCategory.name. Multiple values are passed using comma separated.
     * @param filter Object containing the filters as property values
     * @param initialPath Nested path
     * @returns an string containing the filter in a request format
     */
    buildFilterRequest(filter, initialPath = '') {
        let params = [];
        for (var field in filter) {
            if (filter[field] != undefined) {
                let fieldParam;
                if (Array.isArray(filter[field])) {
                    let values = "";
                    filter[field].forEach((a, index) => {
                        if (typeof a === "string") {
                            a = "'" + a.replace("'", "''") + "'";
                        }
                        if (index > 0) {
                            values = values + "," + a;
                        }
                        else {
                            values = a + "";
                        }
                    });
                    if (values) {
                        fieldParam = initialPath + field + ' eq ' + '"' + encodeURI(values) + '"';
                    }
                }
                else if (typeof filter[field] === "object") {
                    if (filter[field] instanceof Date) {
                        fieldParam = initialPath + field + ' eq "' + filter[field].toISOString() + '"';
                    }
                    else {
                        fieldParam = this.buildFilterRequest(filter[field], initialPath + (initialPath ? '.' : "") + field + ".");
                    }
                }
                else {
                    let quoted = typeof filter[field] !== "number";
                    let filterValue = encodeURI(filter[field] + "");
                    fieldParam = initialPath + field + ' eq ' + (quoted ? '"' : '') + filterValue + (quoted ? '"' : '');
                }
                if (fieldParam) {
                    params.push(fieldParam);
                }
            }
        }
        if (params.length > 0) {
            return params.join(",");
        }
        else {
            return null;
        }
    }
    buildSortRequest(sortFields) {
        let params = sortFields.map(function (sort) {
            return (sort.dir == "desc" ? "-" : "") + sort.field;
        });
        if (params.length > 0) {
            return params.join(",");
        }
        else {
            return null;
        }
    }
    canSearch() {
        return SearchService.canSearch(this.configService, this.nameSpace + "/" + this.resource);
    }
    static canSearch(configService, resource) {
        return configService.getString("resourcePrivilege/search/" + resource).then((privMask) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "view")) : false);
    }
}
/**
 * Static URL prefix for all server side calls
 * @type {string}
 */
SearchService.URL_PREFIX = '/dataserver/search/';
/**
 * Provider lookup key alias that is used during dependency injection
 * @type {OpaqueToken}
 */
SearchService.SEARCH_RESOURCE = new OpaqueToken('searchResource');
/**
 * Provider lookup key alias that is used during dependency injection
 * @type {OpaqueToken}
 */
SearchService.RESOURCE_VERSION = new OpaqueToken('resourceVersion');
SearchService.decorators = [
    { type: Injectable },
];
/** @nocollapse */
SearchService.ctorParameters = () => [
    { type: JwtHttpClientService, },
    { type: ConfigurationService, },
    { type: undefined, decorators: [{ type: Inject, args: [ResourceModule.RESOURCE_NAMESPACE_KEY,] },] },
    { type: Array, decorators: [{ type: Inject, args: [ResourceModule.SEARCH_NAMES_KEY,] },] },
    { type: undefined, decorators: [{ type: Inject, args: [SearchService.SEARCH_RESOURCE,] },] },
    { type: undefined, decorators: [{ type: Inject, args: [SearchService.RESOURCE_VERSION,] },] },
];
//# sourceMappingURL=search.service.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@fedelta-pos/data-client/resource/search/search.service.js
// module id = 422
// module chunks = 1